// src/hooks/dashboard/useSectorDashboardState.ts
import { useMemo } from "react";
import { useDashboardData } from "./useDashboardData";
import { isDashboardResponse } from "@/types/dashboardResponse";
import {
  CHART_METRICS_OPTIONS,
  CHART_RATIOS_OPTIONS,
  CHART_DERIVATIVES_OPTIONS,
} from "@/constants/metrics";
import type { DashboardType } from "@/types/dashboards/dashboardTypes";

interface AvailableCharts {
  metrics: string[];
  ratios: string[];
  derivatives: string[];
}

interface UseSectorDashboardStateResult {
  availableCharts: AvailableCharts;
  presentFields: Set<string>;
  isLoading: boolean;
}

/**
 * Hook for managing sector dashboard state.
 * Determines which chart metrics are available based on fetched data.
 * 
 * @example
 * const { availableCharts, isLoading } = useSectorDashboardState('layer1');
 */
export function useSectorDashboardState(
  dashboardType: DashboardType
): UseSectorDashboardStateResult {
  const metricsQuery = useDashboardData(dashboardType, "metrics");
  const ratiosQuery = useDashboardData(dashboardType, "ratios");

  // Compute available fields from data (exclude null/undefined/NaN values)
  const presentFields = useMemo(() => {
    const nonNull = new Set<string>();

    const processResponse = (resp: unknown) => {
      if (!isDashboardResponse(resp)) return;
      for (const r of resp.data as Record<string, unknown>[]) {
        const rec = r || {};
        for (const [k, v] of Object.entries(rec)) {
          if (v === null || v === undefined) continue;
          if (typeof v === "number" && !Number.isFinite(v)) continue;
          nonNull.add(k);
        }
      }
    };

    processResponse(metricsQuery.data);
    processResponse(ratiosQuery.data);

    return nonNull;
  }, [metricsQuery.data, ratiosQuery.data]);

  // Filter chart options to only include those with available data
  const availableCharts = useMemo<AvailableCharts>(
    () => ({
      metrics: CHART_METRICS_OPTIONS
        .filter((m) => presentFields.has(m.id))
        .map((m) => m.id),
      ratios: CHART_RATIOS_OPTIONS
        .filter((r) => presentFields.has(r.id))
        .map((r) => r.id),
      derivatives: CHART_DERIVATIVES_OPTIONS
        .filter((m) => presentFields.has(m.id))
        .map((m) => m.id),
    }),
    [presentFields]
  );

  return {
    availableCharts,
    presentFields,
    isLoading: metricsQuery.isLoading || ratiosQuery.isLoading,
  };
}

export default useSectorDashboardState;

